import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { youthAPI, problemsAPI, migrantsAPI, eventsAPI, questionsAPI } from '../services/api';
import Icon from '../components/Icon';

function Donut({ data, size=110, thickness=18, center }) {
  const r=(size-thickness)/2, circ=2*Math.PI*r;
  const total=data.reduce((s,d)=>s+d.value,0);
  let offset=0;
  return (
    <div style={{position:'relative',width:size,height:size}}>
      <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={thickness}/>
        {data.filter(d=>d.value>0).map((d,i)=>{
          const dash=(d.value/(total||1))*circ;
          const el=<circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={d.color} strokeWidth={thickness-2}
            strokeDasharray={`${Math.max(0,dash-2)} ${circ-Math.max(0,dash-2)}`} strokeDashoffset={-offset} strokeLinecap="round" style={{transition:'all .8s'}}/>;
          offset+=dash; return el;
        })}
      </svg>
      {center&&<div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>{center}</div>}
    </div>
  );
}

function Gauge({ value, max, label, color }) {
  const pct=max>0?Math.round(value/max*100):0;
  const circ=Math.PI*48;
  return (
    <div style={{textAlign:'center'}}>
      <svg width={100} height={60} viewBox="0 0 120 68">
        <path d="M 12 60 A 48 48 0 0 1 108 60" fill="none" stroke="#f1f5f9" strokeWidth="9" strokeLinecap="round"/>
        <path d="M 12 60 A 48 48 0 0 1 108 60" fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${circ}`} strokeDashoffset={circ-(circ*Math.min(pct,100)/100)} style={{transition:'stroke-dashoffset 1s'}}/>
        <text x="60" y="50" textAnchor="middle" style={{fontSize:20,fontWeight:900,fill:color}}>{pct}%</text>
      </svg>
      <div style={{fontSize:10,fontWeight:600,color:'#64748b',marginTop:-2}}>{label}</div>
    </div>
  );
}

function QuickAction({ icon, label, desc, color, bg, onClick }) {
  return (
    <div onClick={onClick} style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'18px 20px',cursor:'pointer',transition:'all .2s',position:'relative',overflow:'hidden'}}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 25px rgba(0,0,0,0.07)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='none';}}>
      <div style={{position:'absolute',top:-10,right:-10,width:50,height:50,borderRadius:'50%',background:bg,opacity:0.3}}/>
      <div style={{display:'flex',alignItems:'center',gap:12}}>
        <div style={{width:42,height:42,borderRadius:14,background:bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>{icon}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:700,color:'#1e293b'}}>{label}</div>
          <div style={{fontSize:11,color:'#94a3b8'}}>{desc}</div>
        </div>
        <Icon name="chevronRight" size={16} color="#cbd5e1" />
      </div>
    </div>
  );
}

export default function MFYDashboardPage() {
  const [ys, setYs] = useState({});
  const [ps, setPs] = useState({});
  const [ms, setMs] = useState({});
  const [es, setEs] = useState({});
  const [qs, setQs] = useState({});
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      youthAPI.stats().then(r=>setYs(r.data)),
      youthAPI.list().then(r=>setRecent((r.data?.results||r.data||[]).slice(0,8))),
      problemsAPI.stats().then(r=>setPs(r.data)),
      migrantsAPI.stats().then(r=>setMs(r.data)),
      eventsAPI.stats().then(r=>setEs(r.data)),
      questionsAPI.stats().then(r=>setQs(r.data)),
    ]).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return (
    <div style={{textAlign:'center',padding:100}}>
      <div style={{display:'inline-block',width:36,height:36,border:'4px solid #dcfce7',borderTopColor:'#22c55e',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const empRate = ys.total>0 ? Math.round((ys.employed||0)/ys.total*100) : 0;
  const probSolve = ps.total>0 ? Math.round(((ps.hal_qilindi||0)+(ps.yopildi||0))/ps.total*100) : 0;
  const qAnswer = qs.total>0 ? Math.round((qs.javob_berildi||0)/qs.total*100) : 0;

  const empLabels = {ishlamoqda:'Ishlamoqda',ishsiz:'Ishsiz',"o'qimoqda":"O'qimoqda",tadbirkor:'Tadbirkor',boshqa:'Boshqa'};
  const empColors = {ishlamoqda:'#22c55e',ishsiz:'#ef4444',"o'qimoqda":'#3b82f6',tadbirkor:'#f59e0b',boshqa:'#94a3b8'};

  return (
    <div>
      {/* ===== HERO ===== */}
      <div style={{
        background:'linear-gradient(135deg, #14532d 0%, #166534 40%, #22c55e 100%)',
        borderRadius:24,padding:'28px 32px',marginBottom:24,position:'relative',overflow:'hidden'
      }}>
        <div style={{position:'absolute',top:-50,right:-30,width:200,height:200,borderRadius:'50%',background:'radial-gradient(circle,rgba(34,197,94,0.3) 0%,transparent 70%)'}}/>
        <div style={{position:'absolute',bottom:-30,left:'40%',width:140,height:140,borderRadius:'50%',background:'radial-gradient(circle,rgba(74,222,128,0.2) 0%,transparent 70%)'}}/>
        <div style={{position:'relative'}}>
          <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:16}}>
            <div style={{width:50,height:50,borderRadius:16,background:'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24}}>🏠</div>
            <div>
              <h2 style={{fontSize:20,fontWeight:900,color:'#fff',margin:0}}>MFY boshqaruv paneli</h2>
              <p style={{fontSize:12,color:'#bbf7d0',margin:0}}>Mahalla yoshlari bo'yicha ko'rsatkichlar</p>
            </div>
          </div>
          <div className="m-hero" style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8}}>
            {[
              {v:ys.total||0,l:'Yoshlar',e:'👥'},
              {v:ps.total||0,l:'Muammolar',e:'📋'},
              {v:ms.total_migrants||0,l:'Migrantlar',e:'✈️'},
              {v:es.total||0,l:'Tadbirlar',e:'🎯'},
              {v:qs.total||0,l:'Savollar',e:'💬'},
            ].map(s=>(
              <div key={s.l} style={{padding:10,borderRadius:12,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.1)',textAlign:'center'}}>
                <div style={{fontSize:15}}>{s.e}</div>
                <div style={{fontSize:18,fontWeight:900,color:'#fff',lineHeight:1.2}}>{s.v}</div>
                <div style={{fontSize:9,color:'#86efac',fontWeight:500}}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== GAUGES ===== */}
      <div className="m-g3" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:20}}>
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:16,textAlign:'center'}}>
          <Gauge value={empRate} max={100} label="Bandlik darajasi" color={empRate>50?'#22c55e':empRate>30?'#f59e0b':'#ef4444'} />
          <div style={{fontSize:10,color:'#94a3b8',marginTop:4}}>{ys.employed||0}/{ys.total||0} band</div>
        </div>
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:16,textAlign:'center'}}>
          <Gauge value={probSolve} max={100} label="Muammo hal qilish" color={probSolve>60?'#22c55e':probSolve>30?'#f59e0b':'#ef4444'} />
          <div style={{fontSize:10,color:'#94a3b8',marginTop:4}}>{(ps.hal_qilindi||0)+(ps.yopildi||0)}/{ps.total||0} hal</div>
        </div>
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:16,textAlign:'center'}}>
          <Gauge value={qAnswer} max={100} label="Savolga javob" color={qAnswer>60?'#22c55e':qAnswer>30?'#f59e0b':'#ef4444'} />
          <div style={{fontSize:10,color:'#94a3b8',marginTop:4}}>{qs.javob_berildi||0}/{qs.total||0} javob</div>
        </div>
      </div>

      {/* ===== CHARTS ROW ===== */}
      <div className="m-g2" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
        {/* Bandlik */}
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'20px 24px'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
            <span style={{fontSize:16}}>💼</span>
            <span style={{fontSize:14,fontWeight:700,color:'#1e293b'}}>Bandlik tarkibi</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <Donut data={[
              {value:ys.employed||0,color:'#22c55e'},
              {value:ys.unemployed||0,color:'#ef4444'},
              {value:ys.student||0,color:'#3b82f6'},
              {value:ys.entrepreneur||0,color:'#f59e0b'},
              {value:Math.max(0,(ys.total||0)-(ys.employed||0)-(ys.unemployed||0)-(ys.student||0)-(ys.entrepreneur||0)),color:'#94a3b8'},
            ]} center={<><div style={{fontSize:20,fontWeight:900}}>{ys.total||0}</div><div style={{fontSize:8,color:'#94a3b8'}}>jami</div></>} />
            <div style={{flex:1}}>
              {[
                {l:'Ishlamoqda',v:ys.employed||0,c:'#22c55e'},
                {l:'Ishsiz',v:ys.unemployed||0,c:'#ef4444'},
                {l:"O'qimoqda",v:ys.student||0,c:'#3b82f6'},
                {l:'Tadbirkor',v:ys.entrepreneur||0,c:'#f59e0b'},
              ].map(x=>(
                <div key={x.l} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 0'}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:x.c,flexShrink:0}}/>
                  <span style={{fontSize:11,color:'#64748b',flex:1}}>{x.l}</span>
                  <span style={{fontSize:12,fontWeight:800,color:x.c}}>{x.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Jinsi */}
        <div style={{background:'#fff',borderRadius:16,border:'1px solid #e2e8f0',padding:'20px 24px'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
            <span style={{fontSize:16}}>👥</span>
            <span style={{fontSize:14,fontWeight:700,color:'#1e293b'}}>Jins va ijtimoiy holat</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:16}}>
            <Donut data={[
              {value:ys.male||0,color:'#3b82f6'},
              {value:ys.female||0,color:'#ec4899'},
            ]} size={90} thickness={16} center={<div style={{fontSize:10,fontWeight:700,color:'#64748b'}}>{ys.male||0}:{ys.female||0}</div>} />
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:6,padding:'4px 0'}}>
                <span style={{width:8,height:8,borderRadius:'50%',background:'#3b82f6'}}/>
                <span style={{fontSize:11,color:'#64748b',flex:1}}>Erkak</span>
                <span style={{fontSize:12,fontWeight:800,color:'#3b82f6'}}>{ys.male||0}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:6,padding:'4px 0'}}>
                <span style={{width:8,height:8,borderRadius:'50%',background:'#ec4899'}}/>
                <span style={{fontSize:11,color:'#64748b',flex:1}}>Ayol</span>
                <span style={{fontSize:12,fontWeight:800,color:'#ec4899'}}>{ys.female||0}</span>
              </div>
            </div>
          </div>
          {/* Social stats */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:6}}>
            {[
              {l:'Kam tamin.',v:ys.low_income||0,c:'#ca8a04',bg:'#fffbeb',i:'🏠'},
              {l:'Nogiron',v:ys.disabled||0,c:'#64748b',bg:'#f1f5f9',i:'♿'},
              {l:'Yetim',v:ys.orphan||0,c:'#ca8a04',bg:'#fffbeb',i:'👤'},
              {l:'Profilaktik',v:ys.prev_registered||0,c:'#dc2626',bg:'#fef2f2',i:'🔒'},
            ].map(x=>(
              <div key={x.l} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 8px',borderRadius:8,background:x.bg}}>
                <span style={{fontSize:12}}>{x.i}</span>
                <span style={{fontSize:10,color:'#64748b',flex:1}}>{x.l}</span>
                <span style={{fontSize:12,fontWeight:800,color:x.c}}>{x.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== QUICK ACTIONS ===== */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:'#1e293b',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16}}>⚡</span> Tezkor amallar
        </div>
        <div className="m-g2" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <QuickAction icon="👤" label="Yosh qo'shish" desc="Yangi yoshni reestrga kiritish" color="#22c55e" bg="#f0fdf4" onClick={()=>navigate('/youth')} />
          <QuickAction icon="📋" label="Muammo yuborish" desc={`${ps.yangi||0} ta yangi muammo`} color="#8b5cf6" bg="#f5f3ff" onClick={()=>navigate('/problems')} />
          <QuickAction icon="💬" label="Hokimga savol" desc={`${qs.total||0} ta savol yuborilgan`} color="#6366f1" bg="#eef2ff" onClick={()=>navigate('/hokim')} />
          <QuickAction icon="🎯" label="Tadbir yaratish" desc={`${es.total||0} ta tadbir rejada`} color="#0ea5e9" bg="#e0f2fe" onClick={()=>navigate('/events')} />
        </div>
      </div>

      {/* ===== MODULE SUMMARY ===== */}
      <div className="m-g4" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
        {[
          {icon:'📋',label:'Ochiq muammolar',v:ps.yangi||0+ps.jarayonda||0,c:'#8b5cf6',bg:'#f5f3ff'},
          {icon:'⏰',label:"Kechikkan",v:ps.overdue||0,c:'#ef4444',bg:'#fef2f2'},
          {icon:'🤝',label:'Tadbir ishtirok.',v:es.total_participants||0,c:'#0ea5e9',bg:'#e0f2fe'},
          {icon:'✈️',label:'Chetdagi migrant',v:ms.abroad||0,c:'#f59e0b',bg:'#fffbeb'},
        ].map(s=>(
          <div key={s.label} style={{display:'flex',alignItems:'center',gap:10,padding:'14px 16px',borderRadius:14,background:'#fff',border:'1px solid #e2e8f0'}}>
            <div style={{width:38,height:38,borderRadius:12,background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17}}>{s.icon}</div>
            <div>
              <div style={{fontSize:18,fontWeight:900,color:s.c,lineHeight:1}}>{s.v}</div>
              <div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ===== RECENT YOUTH ===== */}
      <div style={{borderRadius:16,background:'#fff',border:'1px solid #e2e8f0',overflow:'hidden'}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:16}}>👥</span>
            <span style={{fontSize:14,fontWeight:700,color:'#1e293b'}}>So'nggi qo'shilgan yoshlar</span>
          </div>
          <button onClick={()=>navigate('/youth')} style={{padding:'6px 14px',borderRadius:10,border:'1px solid #e2e8f0',background:'#f8fafc',fontSize:12,fontWeight:600,color:'#6366f1',cursor:'pointer',fontFamily:'inherit'}}>
            Barchasini ko'rish →
          </button>
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:500}}>
            <thead>
              <tr style={{borderBottom:'1px solid #e2e8f0'}}>
                {['F.I.O','Yosh','Jinsi','Bandlik','Sana'].map(h=>(
                  <th key={h} style={{padding:'10px 16px',textAlign:'left',fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.3px'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map(y=>{
                const empColor = y.emp_status==='ishsiz'?'#ef4444':y.emp_status==='ishlamoqda'?'#22c55e':y.emp_status==="o'qimoqda"?'#3b82f6':'#94a3b8';
                const empBg = y.emp_status==='ishsiz'?'#fef2f2':y.emp_status==='ishlamoqda'?'#f0fdf4':y.emp_status==="o'qimoqda"?'#dbeafe':'#f1f5f9';
                return (
                  <tr key={y.id} style={{borderBottom:'1px solid #f1f5f9',transition:'background .15s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#fafbff'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{padding:'10px 16px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{width:30,height:30,borderRadius:8,background:'linear-gradient(135deg,#22c55e,#4ade80)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:'#fff'}}>
                          {y.full_name?.split(' ').map(w=>w[0]).join('').slice(0,2)}
                        </div>
                        <span style={{fontSize:13,fontWeight:600,color:'#1e293b'}}>{y.full_name}</span>
                      </div>
                    </td>
                    <td style={{padding:'10px 16px',fontSize:13,color:'#64748b'}}>{y.age}</td>
                    <td style={{padding:'10px 16px'}}>
                      <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:6,background:y.gender==='male'?'#dbeafe':'#fce7f3',color:y.gender==='male'?'#2563eb':'#db2777'}}>
                        {y.gender==='male'?'Erkak':'Ayol'}
                      </span>
                    </td>
                    <td style={{padding:'10px 16px'}}>
                      <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:6,background:empBg,color:empColor}}>
                        {empLabels[y.emp_status]||y.emp_status||'—'}
                      </span>
                    </td>
                    <td style={{padding:'10px 16px',fontSize:11,color:'#94a3b8'}}>{y.created_at?.slice(0,10)}</td>
                  </tr>
                );
              })}
              {recent.length===0&&<tr><td colSpan={5} style={{padding:40,textAlign:'center',color:'#94a3b8',fontSize:13}}>Hozircha yoshlar yo'q</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .m-hero{grid-template-columns:repeat(5,1fr)}
        .m-g4{grid-template-columns:repeat(4,1fr)}
        .m-g3{grid-template-columns:repeat(3,1fr)}
        .m-g2{grid-template-columns:1fr 1fr}
        @media(max-width:1100px){.m-hero{grid-template-columns:repeat(3,1fr)}.m-g4{grid-template-columns:repeat(2,1fr)}.m-g3{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:768px){.m-g2{grid-template-columns:1fr}.m-g4{grid-template-columns:repeat(2,1fr)}.m-g3{grid-template-columns:1fr}.m-hero{grid-template-columns:repeat(2,1fr)}}
      `}</style>
    </div>
  );
}
